// src/features/websites/components/DomSelectorFields.tsx
import React, { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { testWebsiteSelectors } from '../api';
import AIHeader from 'components/common/AIHeader';
import { AIGenerateCssSelectors } from 'features/publishers/api';

interface DomSelectorFieldsProps {
  selectors: {
    articleContainer?: string;
    headline?: string;
    subheadline?: string;
    content?: string;
    publishDate?: string;
    author?: string;
    image?: string;
    category?: string;
    fullArticleContent?: string;
    fullArticleImage?: string;
  };
  websitePK: string;
  websiteUrl: string;
  headlinesUrl: string;
  errors: Record<string, string>;
  isLoading: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectorsChange?: (newSelectors: Record<string, string>) => void;
}

const DomSelectorFields: React.FC<DomSelectorFieldsProps> = ({
  selectors,
  websitePK,
  websiteUrl,
  headlinesUrl,
  errors,
  isLoading,
  onChange,
  onSelectorsChange,
}) => {
  const [isTestingSelectors, setIsTestingSelectors] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleTestSelectors = async () => {
    if (!websiteUrl || !selectors) {
      return;
    }

    setIsTestingSelectors(true);
    setTestResults(null);

    try {
      // Call the API to test the selectors
      const results = await testWebsiteSelectors(websitePK, selectors);
      setTestResults(results);
    } catch (error) {
      setTestResults({
        success: false,
        error: "Failed to test selectors. Please check the URL and selectors."
      });
    } finally {
      setIsTestingSelectors(false);
    }
  };

  const handleGenerateCssSelectors = async () => {
    setIsGenerating(true);

    try {
      const response = await AIGenerateCssSelectors(websitePK, headlinesUrl);
      const newSelectors = response.selectors;
      console.log(newSelectors);

      // Use the bulk update function if available
      if (onSelectorsChange) {
        onSelectorsChange(newSelectors);
      } else {
        // Fallback to the original approach (which has the issue)
        Object.entries(newSelectors).forEach(([key, value]) => {
          onChange({
            target: {
              name: `selectors.${key}`,
              value: value,
            },
          } as React.ChangeEvent<HTMLInputElement>);
        });
      }
    } catch (error) {
      console.error("Error generating CSS selectors:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>

      <Box sx={{ mb: 4 }}>
        <AIHeader
          title="CSS Selectors"
          description="Define CSS selectors to identify content elements on the website."
          dialogTitle="Generate CSS selectors with AI"
          dialogDescription="This operation will use AI to generate the CSS selectors, based on the Headlines URL you entered. The process may take several minutes to complete. You can wait or return later to see the results."
          progressMessage="AI is generating your CSS selectors based on Headlines URL you entered. This will take a few minutes to complete."
          onGenerateConfirm={handleGenerateCssSelectors}
          isGenerating={isGenerating}
          setIsGenerating={setIsGenerating}
          generateButtonText="Generate"
          cancelButtonText="Cancel"
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Article Container"
            name="selectors.articleContainer"
            value={selectors?.articleContainer || ''}
            onChange={onChange}
            error={!!errors['selectors.articleContainer']}
            helperText={errors['selectors.articleContainer'] || "Selector for the container of each article (e.g., 'article.post')"}
            disabled={isLoading}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Headline"
            name="selectors.headline"
            value={selectors?.headline || ''}
            onChange={onChange}
            error={!!errors['selectors.headline']}
            helperText={errors['selectors.headline'] || "Selector for the article headline (e.g., 'h2.entry-title a')"}
            disabled={isLoading}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Subheadline"
            name="selectors.subheadline"
            value={selectors?.subheadline || ''}
            onChange={onChange}
            helperText="Selector for article subheadline (if applicable)"
            disabled={isLoading}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Content"
            name="selectors.content"
            value={selectors?.content || ''}
            onChange={onChange}
            error={!!errors['selectors.content']}
            helperText={errors['selectors.content'] || "Selector for article content (e.g., 'div.entry-content p')"}
            disabled={isLoading}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Publish Date"
            name="selectors.publishDate"
            value={selectors?.publishDate || ''}
            onChange={onChange}
            helperText="Selector for article publish date"
            disabled={isLoading}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Author"
            name="selectors.author"
            value={selectors?.author || ''}
            onChange={onChange}
            helperText="Selector for article author"
            disabled={isLoading}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Image"
            name="selectors.image"
            value={selectors?.image || ''}
            onChange={onChange}
            helperText="Selector for article image"
            disabled={isLoading}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Category"
            name="selectors.category"
            value={selectors?.category || ''}
            onChange={onChange}
            helperText="Selector for article category"
            disabled={isLoading}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Full Article Content"
            name="selectors.fullArticleContent"
            value={selectors?.fullArticleContent || ''}
            onChange={onChange}
            helperText="Selector for content on the full article page"
            disabled={isLoading}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Full Article Image"
            name="selectors.fullArticleImage"
            value={selectors?.fullArticleImage || ''}
            onChange={onChange}
            helperText="Selector for images on the full article page"
            disabled={isLoading}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2">
              Test selectors against the website
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleTestSelectors}
              disabled={isLoading || isTestingSelectors || !websiteUrl}
              startIcon={isTestingSelectors ? <CircularProgress size={20} /> : null}
            >
              {isTestingSelectors ? 'Testing...' : 'Test Selectors'}
            </Button>
          </Box>

          {testResults && (
            <Box sx={{ mt: 2 }}>
              {testResults.success ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Successfully found {testResults.articlesFound} articles on the page.
                </Alert>
              ) : (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {testResults.error}
                </Alert>
              )}

              {testResults.success && testResults.previewArticles && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Content Preview (First {testResults.previewArticles.length} Articles)
                  </Typography>

                  {testResults.previewArticles.map((article: any, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 2,
                        pb: 2,
                        borderBottom: index < testResults.previewArticles.length - 1 ? (theme => `1px solid ${theme.palette.divider}`) : 'none'
                      }}
                    >
                      <Typography variant="h6" gutterBottom>
                        <a href={article.originalUrl} target="_blank" rel="noopener noreferrer">{article.headline}</a>
                      </Typography>

                      {article.subheadline && (
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                          {article.subheadline}
                        </Typography>
                      )}

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {article.publishDate && `Published: ${article.publishDate}`}
                        {article.publishDate && article.author && ' | '}
                        {article.author && `By: ${article.author}`}
                      </Typography>

                      <Typography variant="body1">
                        {article.contentPreview}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default DomSelectorFields;